const WALL_CLOCK_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const WALL_CLOCK_TIME = /^(\d{2}):(\d{2})(?::(\d{2}))?$/;
const DAY_MS = 24 * 60 * 60 * 1000;

const formatters = new Map<string, Intl.DateTimeFormat>();

function zoneFormatter(zone: string): Intl.DateTimeFormat {
  const cached = formatters.get(zone);
  if (cached) return cached;
  // Throws RangeError on an unknown IANA zone, which the caller turns into null.
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: zone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  formatters.set(zone, formatter);
  return formatter;
}

/** The zone's wall clock at `instant`, as the UTC epoch ms of that wall clock. */
function wallClockAt(instant: number, formatter: Intl.DateTimeFormat): number {
  const parts: Record<string, string> = {};
  for (const part of formatter.formatToParts(new Date(instant))) {
    parts[part.type] = part.value;
  }
  const hour = Number(parts.hour);
  return Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    // Some ICU builds still render midnight as hour 24 under h23.
    hour === 24 ? 0 : hour,
    Number(parts.minute),
    Number(parts.second),
  );
}

/**
 * The instant a booked `YYYY-MM-DD` + `HH:mm` wall clock names in the
 * schedule's own zone — the zone the API books it in (`resolveScheduleTimezone`
 * plus `toUTC`), never the customer's device zone.
 *
 * The resolution is a search over the zone's own offsets, not a library
 * shortcut, so it does not depend on the runtime's clock: dayjs's `timezone`
 * plugin seeds an ambiguous wall clock with the zone's offset *today*, which
 * made a repeated DST hour land an hour apart depending on the month the
 * customer booked in. An hour that happens twice resolves to its **first**
 * occurrence, as the API's `toUTC` does (`appointments/time.ts`).
 *
 * Returns null when the zone is missing or unknown, when the wall clock is
 * unusable, and for a wall clock the zone skips over at a spring-forward (one
 * the API refuses to book, `INVALID_LOCAL_TIME`), so callers keep their
 * previous behavior instead of guessing (BF-DRIFT-016).
 */
export function scheduleWallClockToInstant(
  date: string,
  time: string,
  timeZone: string | null | undefined,
): Date | null {
  const zone = timeZone?.trim();
  if (!zone) return null;

  const dateParts = WALL_CLOCK_DATE.exec(date.trim());
  const timeParts = WALL_CLOCK_TIME.exec(time.trim());
  if (!dateParts || !timeParts) return null;

  const year = Number(dateParts[1]);
  const month = Number(dateParts[2]);
  const day = Number(dateParts[3]);
  const hour = Number(timeParts[1]);
  const minute = Number(timeParts[2]);
  const second = Number(timeParts[3] ?? "0");
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  if (hour > 23 || minute > 59 || second > 59) return null;

  const wallClock = Date.UTC(year, month - 1, day, hour, minute, second);
  const normalized = new Date(wallClock);
  // Rejects a day the month does not have (Date.UTC rolls it over).
  if (normalized.getUTCMonth() !== month - 1 || normalized.getUTCDate() !== day) {
    return null;
  }

  let formatter: Intl.DateTimeFormat;
  try {
    formatter = zoneFormatter(zone);
  } catch {
    return null;
  }

  // The true instant is `wallClock` minus the zone's offset then, so it sits
  // within the offsets in force around this first guess. Probing a day either
  // side of it covers both sides of a transition near it.
  const guess = wallClock - (wallClockAt(wallClock, formatter) - wallClock);
  const offsets = new Set<number>();
  for (const probe of [guess - DAY_MS, guess, guess + DAY_MS]) {
    offsets.add(wallClockAt(probe, formatter) - probe);
  }

  let instant: number | null = null;
  for (const offset of offsets) {
    const candidate = wallClock - offset;
    // Keep only a candidate whose wall clock in the zone is the one asked for;
    // a skipped spring-forward hour leaves none.
    if (wallClockAt(candidate, formatter) !== wallClock) continue;
    if (instant === null || candidate < instant) instant = candidate;
  }

  return instant === null ? null : new Date(instant);
}
